import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './ClassDetails.css';
import { getApiUrl, API_ENDPOINTS } from '../../../config/api.config';
import { getAuthHeaders } from '../../../utils/api.js';
import { toggleEnvironment } from '../../../utils/apiClient';

const ClassDetails = () => {
  const [questions, setQuestions] = useState([]);
  const [classDetails, setClassDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Extract the class ID - route is "/student/class/:classId"
  const { classId } = useParams();
  
  // Log URL and params for debugging
  console.log('URL Path:', window.location.pathname);
  console.log('Class ID from params:', classId);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { className, teacherName } = location.state || {};

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      // Check if classId exists and is valid
      if (!classId || classId === 'undefined') {
        console.error('Invalid class ID:', classId);
        setError('Invalid class ID. Please go back and try again.');
        setLoading(false);
        return;
      }

      console.log('Fetching data for class ID:', classId);
      
      try {
        // First fetch class details if not provided in state
        let classData = null;
        if (!className || !teacherName) {
          try {
            const classResponse = await axios.get(
              `/api/v1/stem@home/class/${classId}`,
              { 
                baseURL: toggleEnvironment(),
                headers: getAuthHeaders() 
              }
            );
            
            console.log('Class response:', classResponse.data);
            
            if (classResponse.data.success) {
              classData = classResponse.data.class;
              if (isMounted) {
                setClassDetails(classData);
              }
            }
          } catch (classError) {
            console.error('Error fetching class details:', classError);
            // Continue even if class details fail - we might have them from state
          }
        }

        // Then fetch questions for this class
        try {
          console.log(`Fetching questions for class ID: ${classId} - URL: /api/v1/stem@home/class/${classId}/questions`);
          
          const questionsResponse = await axios.get(
            `/api/v1/stem@home/class/${classId}/questions`,
            { 
              baseURL: toggleEnvironment(),
              headers: getAuthHeaders() 
            }
          );

          console.log('Questions response:', questionsResponse.data);

          if (questionsResponse.data.success && questionsResponse.data.questions) {
            if (isMounted) {
              setQuestions(questionsResponse.data.questions);
              console.log('Questions loaded:', questionsResponse.data.questions.length);
            }
          } else {
            console.warn('No questions found in response or invalid format');
            if (isMounted) {
              setQuestions([]);
            }
          }
        } catch (questionsError) {
          console.error('Error fetching questions:', questionsError);
          if (isMounted) {
            setError('Failed to load questions. Please try again.');
          }
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        if (isMounted) {
          setError(error.response?.data?.message || error.message || 'Failed to load class data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [classId, className, teacherName]);

  const handleStartSubmission = () => {
    // Navigate to the question view with the class info
    navigate(`/start-submission/${classId}`, {
      state: {
        className: className || classDetails?.name
      }
    });
  };

  if (loading) {
    return (
      <div className="class-details-container">
        <div className="loading">Loading class details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="class-details-container">
        <div className="error">{error}</div>
        <button onClick={() => navigate('/my-classes')} className="back-button">
          Back to Classes
        </button>
      </div>
    );
  }

  // Use either state values or fetched class details
  const displayClassName = className || classDetails?.name || 'Class';
  const displayTeacherName = teacherName || classDetails?.teacher?.name || 'Not assigned';

  console.log('Questions rendering:', questions);

  if (!questions || questions.length === 0) {
    return (
      <div className="class-details-container">
        <div className="questions-overview">
          <h1 className="class-title">{displayClassName}</h1>
          <div className="class-info">
            <div className="info-line">Teacher: {displayTeacherName}</div>
          </div>
          <div className="no-questions">
            <p>No questions available for this class yet.</p>
            <button onClick={() => navigate('/my-classes')} className="back-button">
              Back to Classes
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="class-details-container">
      <div className="questions-overview">
        <h1 className="class-title">{displayClassName}</h1>
        <div className="class-info">
          <div className="info-line">Teacher: {displayTeacherName}</div>
          <div className="info-line">Total Questions: {questions.length}</div>
        </div>
        
        <div className="marks-breakdown">
          {questions.map((question, index) => (
            <div key={question.id || question._id || index} className="question-line">
              <span>Question {index + 1}:</span>
              <span>{question.marks} marks</span>
            </div>
          ))}
        </div>

        <div className="action-section">
          <button 
            className="start-submission-button"
            onClick={handleStartSubmission}
            disabled={questions.length === 0}
          >
            Start Submission
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassDetails; 